import { clamp, mapRange } from './cosmicMath';

export type StarTint = 'white' | 'cyan' | 'magenta' | 'gold';

export interface BackdropStar {
  x: number;
  y: number;
  radius: number;
  depth: number;
  twinklePhase: number;
  twinkleSpeed: number;
  tint: StarTint;
}

export interface AuroraBand {
  anchorX: number;
  anchorY: number;
  radiusScale: number;
  driftAmplitude: number;
  driftSpeed: number;
  driftPhase: number;
  alpha: number;
  accentMix: number;
}

export interface CometSpawn {
  startX: number;
  startY: number;
  angleRad: number;
  lengthScale: number;
  durationMs: number;
}

const starTintCycle: StarTint[] = ['white', 'white', 'white', 'cyan', 'white', 'magenta', 'white', 'gold'];

function createSeededRandom(seed: number): () => number {
  let state = Math.floor(seed * 2 ** 31) + 1;

  return () => {
    state = Math.imul(48271, state) % 2147483647;
    return (state & 2147483647) / 2147483648;
  };
}

function assertSeed(seed: number) {
  if (seed < 0 || seed >= 1) {
    throw new RangeError('Backdrop seed must be at least 0 and less than 1.');
  }
}

export function getStarCountForPointBudget(galaxyPointCount: number): number {
  return Math.round(clamp(galaxyPointCount / 4, 90, 240));
}

export function createStarField(seed: number, count: number): BackdropStar[] {
  assertSeed(seed);

  if (count < 0) {
    throw new RangeError('Star count must not be negative.');
  }

  const random = createSeededRandom(seed);

  return Array.from({ length: count }, (_, index) => {
    const depth = random();

    return {
      x: random(),
      y: random(),
      radius: mapRange(depth, 0, 1, 0.5, 1.7) * (0.75 + random() * 0.5),
      depth,
      twinklePhase: random() * Math.PI * 2,
      twinkleSpeed: 0.35 + random() * 1.05,
      tint: starTintCycle[index % starTintCycle.length],
    };
  });
}

export function createAuroraBands(seed: number, count = 3): AuroraBand[] {
  assertSeed(seed);

  const random = createSeededRandom(seed);

  return Array.from({ length: Math.max(0, count) }, (_, index) => ({
    anchorX: 0.14 + (index / Math.max(1, count)) * 0.72 + random() * 0.1,
    anchorY: 0.16 + random() * 0.5,
    radiusScale: 0.5 + random() * 0.55,
    driftAmplitude: 0.03 + random() * 0.05,
    driftSpeed: 0.05 + random() * 0.09,
    driftPhase: random() * Math.PI * 2,
    alpha: 0.1 + random() * 0.12,
    accentMix: index === 0 ? 1 : random() * 0.6,
  }));
}

export function getStarTwinkle(phase: number, speed: number, elapsedSeconds: number): number {
  return mapRange(Math.sin(phase + elapsedSeconds * speed), -1, 1, 0.3, 1);
}

export function getParallaxShift(depth: number, pointer: number, maxShiftPx = 26): number {
  return clamp(pointer, -1, 1) * depth * maxShiftPx;
}

export function getAuroraDrift(band: AuroraBand, elapsedSeconds: number): { x: number; y: number } {
  const angle = band.driftPhase + elapsedSeconds * band.driftSpeed;

  return {
    x: Math.cos(angle) * band.driftAmplitude,
    y: Math.sin(angle * 0.8) * band.driftAmplitude * 0.6,
  };
}

export function createCometSpawn(seed: number): CometSpawn {
  assertSeed(seed);

  const random = createSeededRandom(seed);

  return {
    startX: random() * 0.9 + 0.05,
    startY: random() * 0.4,
    angleRad: Math.PI * (0.62 + random() * 0.2),
    lengthScale: 0.12 + random() * 0.14,
    durationMs: Math.round(680 + random() * 520),
  };
}

export function getCometIntervalMs(seed: number, cometsEquipped: boolean): number {
  assertSeed(seed);

  return cometsEquipped
    ? Math.round(1400 + seed * 2400)
    : Math.round(7000 + seed * 9000);
}

export function getCometOpacity(progress: number): number {
  if (progress < 0 || progress > 1) {
    throw new RangeError('Comet progress must be between 0 and 1.');
  }

  return progress < 0.2
    ? mapRange(progress, 0, 0.2, 0, 1)
    : mapRange(progress, 0.2, 1, 1, 0);
}

function hueToChannel(p: number, q: number, t: number): number {
  const hue = ((t % 1) + 1) % 1;

  if (hue < 1 / 6) {
    return p + (q - p) * 6 * hue;
  }

  if (hue < 1 / 2) {
    return q;
  }

  if (hue < 2 / 3) {
    return p + (q - p) * (2 / 3 - hue) * 6;
  }

  return p;
}

/** Disco Nebula reward: cycles the aurora accent around the full hue wheel every 10 seconds. */
export function getDiscoAccentRgb(elapsedSeconds: number): string {
  const hue = ((elapsedSeconds * 36) % 360 + 360) % 360 / 360;
  const saturation = 0.9;
  const lightness = 0.62;
  const q = lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  return [
    hueToChannel(p, q, hue + 1 / 3),
    hueToChannel(p, q, hue),
    hueToChannel(p, q, hue - 1 / 3),
  ].map((channel) => Math.round(clamp(channel, 0, 1) * 255)).join(', ');
}

/** Warp Drive reward: how far each star smears into a light-speed streak, by parallax depth. */
export function getWarpStreakScale(depth: number): number {
  return 3 + clamp(depth, 0, 1) * 9;
}
